import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LogOut, Home, User, ChevronDown, Loader2 } from "lucide-react";
import { LogoMark } from "@ava/shared/components/design-system";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

interface AppHeaderProps {
  /** Texto de subtítulo opcional (ej: "Solicitud: ONB-xxx") */
  subtitle?: string;
  /** Mostrar botón de volver al inicio */
  showHomeButton?: boolean;
  /** Callback personalizado para el botón de inicio */
  onHomeClick?: () => void;
}

export function AppHeader({ subtitle, showHomeButton = true, onHomeClick }: AppHeaderProps) {
  const [, setLocation] = useLocation();
  const { user, loading, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    // Full reload (no setLocation) para evitar race con ProtectedRoute,
    // que al ver user=null en la ruta actual dispararía un segundo redirect
    // a getLoginUrl(). El reload limpia estado y aterriza en el Home público.
    window.location.href = "/";
  };

  const handleHomeClick = () => {
    if (onHomeClick) {
      onHomeClick();
    } else {
      setLocation("/");
    }
  };

  // Obtener iniciales del usuario
  const getInitials = (name?: string, email?: string) => {
    if (name) {
      const parts = name.split(" ");
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return "US";
  };

  return (
    <header className="border-b border-slate-200 bg-white/95 backdrop-blur-md sticky top-0 z-50 shadow-sm">
      <div className="container px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo y título */}
          <div className="flex items-center gap-3">
            <div 
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleHomeClick}
            >
              <LogoMark size={32} />
              <div>
                <h1 className="text-xl font-bold text-foreground leading-tight">
                  AVA <span className="font-semibold text-muted-foreground">Onboarding</span>
                </h1>
                {subtitle && (
                  <p className="text-xs text-muted-foreground">{subtitle}</p>
                )}
              </div>
            </div>
          </div>

          {/* Usuario y acciones */}
          <div className="flex items-center gap-3">
            {showHomeButton && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleHomeClick}
                className="hidden sm:flex gap-2"
              >
                <Home className="w-4 h-4" />
                Inicio
              </Button>
            )}

            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Cargando...</span>
              </div>
            ) : user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                        {getInitials(user.name ?? undefined, user.email ?? undefined)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden sm:flex flex-col items-start">
                      <span className="text-sm font-medium truncate max-w-[150px]">
                        {user.name || user.email}
                      </span>
                      {user.name && user.email && (
                        <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                          {user.email}
                        </span>
                      )}
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user.name || "Usuario"}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleHomeClick}>
                    <Home className="w-4 h-4 mr-2" />
                    Ir al Inicio
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Badge variant="outline" className="text-muted-foreground">
                <User className="w-3 h-3 mr-1" />
                No conectado
              </Badge>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
